import type { TFunction } from 'i18next';
import {
  Plus,
  Pencil,
  UserX,
  UserCheck,
  Download,
  MessageSquare,
  Ban,
  BellRing,
  CircleCheck,
  CircleX,
  PowerOff,
  Power,
  Trash2,
  KeyRound,
  RotateCw,
  MailPlus,
  CircleHelp,
  type LucideIcon,
} from 'lucide-react';
import { isMaskedChange, type AdminAuditEvent, type AuditAction, type AuditEventActor } from 'api/adminAuditApi';
import type { ExclusionReason } from 'api/technicianImportContract';
import { REFUSAL_REASON_CODES, type RefusalReasonCode } from 'api/interventionRequestsApi';
import { closedListLabel, exclusionReasonLabel, languageLabel, type ClosedListField } from 'pages/AdminTechnician/validation';
import { refusalReasonLabel } from 'pages/AdminIssues/components/upcomingAssignment';
import { decisionRecipientLabel } from 'pages/AdminGroups/decisionRecipientLabel';
import { windowLabel, type TimeWindow } from 'constants/timeWindows';
import { centsText } from 'utils/money';
import { activeLocale } from 'i18n';
import { PERMISSIONS } from 'utils/permissionUtils';
import { permissionLabel } from 'constants/permissions';
import { ROLES, roleLabel, modeLabel, isValidRole } from 'utils/roleUtils';

/** Entity type of an F9 import's parent row — backend `AdminAuditEvent.IMPORT_PARENT_ENTITY_TYPE`. */
export const TECHNICIAN_IMPORT_ENTITY = 'technician_import';
/** Entity type of a sector realignment run's parent row — backend `AdminAuditEvent.BATCH_PARENTS`. */
export const SECTOR_REALIGNMENT_ENTITY = 'sector_realignment';
/** A realigned ticket's child row — backend `AdminAuditEvent.REALIGNED_TICKET_ENTITY_TYPE`. */
export const REALIGNED_TICKET_ENTITY = 'ticket';
/** A realigned ticket Jira refused (`context.status`) — backend `AdminAuditEvent.REALIGNED_FAILED_STATUS`. */
export const REALIGNED_FAILED_STATUS = 'failed';
/** Parent rows that expand into their batch's children (`ImportRow`). */
export const BATCH_PARENT_ENTITIES: readonly string[] = [TECHNICIAN_IMPORT_ENTITY, SECTOR_REALIGNMENT_ENTITY];

export type AuditTone = 'violet' | 'sky' | 'rose' | 'emerald' | 'amber' | 'zinc';

export interface AuditActionMeta {
  /** Resolver, not a static key: the repo's i18n guard only allows literal `t('...')` calls. */
  label: (t: TFunction) => string;
  icon: LucideIcon;
  tone: AuditTone;
}

/** Taxonomy of §3 — icon + label + tone, never a color alone. */
const AUDIT_ACTION_META: Record<AuditAction, AuditActionMeta> = {
  create: { label: (t) => t('Creation'), icon: Plus, tone: 'violet' },
  update: { label: (t) => t('Change'), icon: Pencil, tone: 'sky' },
  exclude: { label: (t) => t('Exclusion'), icon: UserX, tone: 'rose' },
  reinstate: { label: (t) => t('Reinstatement'), icon: UserCheck, tone: 'emerald' },
  import: { label: (t) => t('Import'), icon: Download, tone: 'amber' },
  note: { label: (t) => t('Comment'), icon: MessageSquare, tone: 'zinc' },
  cancel: { label: (t) => t('Cancellation'), icon: Ban, tone: 'rose' },
  remind: { label: (t) => t('Reminder'), icon: BellRing, tone: 'amber' },
  accept: { label: (t) => t('Acceptance'), icon: CircleCheck, tone: 'emerald' },
  decline: { label: (t) => t('Refusal'), icon: CircleX, tone: 'rose' },
  deactivate: { label: (t) => t('Deactivation'), icon: PowerOff, tone: 'zinc' },
  reactivate: { label: (t) => t('Reactivation'), icon: Power, tone: 'emerald' },
  delete: { label: (t) => t('Deletion'), icon: Trash2, tone: 'rose' },
  grant: { label: (t) => t('Rights change'), icon: KeyRound, tone: 'violet' },
  realign: { label: (t) => t('Realignment'), icon: RotateCw, tone: 'sky' },
  resend_invitation: { label: (t) => t('Invitation resent'), icon: MailPlus, tone: 'sky' },
};

/** CA47 — an action outside the known taxonomy (future lot ahead of this one's i18n) shows its
 * raw key rather than crashing or showing a blank pill. */
export const actionMeta = (action: string): AuditActionMeta =>
  AUDIT_ACTION_META[action as AuditAction] ?? { label: () => action, icon: CircleHelp, tone: 'zinc' };

/** Every entity's snapshot fields (technician, `technicianAuditSnapshot.ts`; intervention_request,
 * Tasks 3-5), reusing each record's own labels rather than a second, drifting copy. */
const FIELD_LABELS: Partial<Record<string, (t: TFunction) => string>> = {
  username: (t) => t('Username'),
  email: (t) => t('Email'),
  lang: (t) => t('Language'),
  isActive: (t) => t('Account status'),
  firstName: (t) => t('First name'),
  lastName: (t) => t('Last name'),
  company: (t) => t('Company'),
  phone: (t) => t('Phone'),
  countryCode: (t) => t('Country'),
  contractType: (t) => t('Contract status'),
  profileKind: (t) => t('Profile'),
  experienceLevel: (t) => t('Level'),
  teamSize: (t) => t('Team size'),
  vehicleType: (t) => t('Vehicle'),
  excludedReason: (t) => t('Reason'),
  opsNote: (t) => t('Internal comment'),
  addresses: (t) => t('Addresses'),
  password: (t) => t('Password'),
  role: (t) => t('Role'),
  mode: (t) => t('Mode'),
  permissions: (t) => t('Additional permissions'),
  serviceDesks: (t) => t('Service desks'),
  ticketCreationDesks: (t) => t('Allowed service desks for ticket creation'),
  sectors: (t) => t('Sectors'),
  specialties: (t) => t('Specialties'),
  expertises: (t) => t('Expertises'),
  geoAreas: (t) => t('Areas covered'),
  secondaryGeoAreas: (t) => t('Secondary zone'),
  status: (t) => t('Status'),
  technician: (t) => t('Technician'),
  earliestDate: (t) => t('Start date'),
  deadlineDate: (t) => t('Deadline'),
  timeWindow: (t) => t('Time window'),
  indicativeTime: (t) => t('Indicative time'),
  usefulInfo: (t) => t('Useful information'),
  tariff: (t) => t('Tariff'),
  tariffBasis: (t) => t('Tariff basis'),
  travelFee: (t) => t('Travel fee'),
  travelFeeBasis: (t) => t('Travel fee basis'),
  sentOutsidePerimeter: (t) => t('Outside perimeter'),
  chosenDate: (t) => t('Chosen date'),
  chosenTime: (t) => t('Chosen time'),
  refusalReasonCode: (t) => t('Reason'),
  refusalReason: (t) => t('Reason details'),
  reminderCount: (t) => t('Reminders'),
  name: (t) => t('Name'),
  active: (t) => t('Status'),
  sortOrder: (t) => t('Display order'),
  isValidated: (t) => t('Validated'),
  postalPrefixes: (t) => t('Postal prefixes'),
  travelZone: (t) => t('Travel zone'),
  // CA26 — subdivisionType's VALUE stays raw (no enumValueLabel branch); only its label is known.
  subdivisionType: (t) => t('Subdivision', { count: 1 }),
  serviceDesk: (t) => t('Service desk'),
  requestType: (t) => t('Request type'),
  gateStatus: (t) => t('Gate status'),
  allowedTransitions: (t) => t('Allowed transitions'),
  decisionRecipients: (t) => t('Decision email recipients'),
  approvalStatuses: (t) => t('Approval'),
  rejectionStatuses: (t) => t('Rejection'),
  members: (t) => t('Members'),
  periodType: (t) => t('Period type'),
  periodDays: (t) => t('Number of days'),
  periodFrom: (t) => t('Start date'),
  periodTo: (t) => t('End date'),
  filters: (t) => t('Filters'),
  limit: (t) => t('Limit'),
  onExceed: (t) => t('Action on exceed'),
  message: (t) => t('Message'),
};

/** CA26 — an unknown field must never crash: its raw key is shown as is. */
export const fieldLabel = (t: TFunction, field: string): string => FIELD_LABELS[field]?.(t) ?? field;

const CLOSED_LIST_FIELD: Partial<Record<string, ClosedListField>> = {
  contractType: 'contract',
  profileKind: 'profile',
  experienceLevel: 'level',
  vehicleType: 'vehicle',
};

/** Intervention request status (`api/interventionRequestsApi.ts`) — a literal switch, not a
 * table, since the i18n guard only allows literal `t('...')` calls. */
const interventionStatusLabel = (t: TFunction, status: string): string => {
  switch (status) {
    case 'pending':
      return t('Pending');
    case 'accepted':
      return t('Accepted');
    case 'declined':
      return t('Declined');
    case 'cancelled':
      return t('Cancelled request');
    case 'superseded':
      return t('Superseded');
    default:
      return status;
  }
};

/** A quota's window shape — a literal switch, not a table (i18n guard). Unknown stays raw (CA26). */
const quotaPeriodLabel = (t: TFunction, value: string): string => {
  switch (value) {
    case 'month':
      return t('Monthly');
    case 'isoQuarter':
      return t('Quarterly');
    case 'rollingDays':
      return t('Rolling days');
    case 'fixed':
      return t('Fixed period');
    case 'none':
      return t('No limit');
    default:
      return value;
  }
};

/** Reuses the technician/intervention-request records' own enum-to-label helpers — never a
 * second, drifting copy. An unknown value stays raw rather than crashing (CA26). */
export const enumValueLabel = (t: TFunction, field: string, value: string): string => {
  const listField = CLOSED_LIST_FIELD[field];
  if (listField) return closedListLabel(t, listField, value);
  if (field === 'excludedReason') return exclusionReasonLabel(t, value as ExclusionReason);
  if (field === 'lang') return languageLabel(t, value);
  if (field === 'isActive' || field === 'active') return value === 'true' ? t('Active') : t('Inactive');
  if (field === 'isValidated') return value === 'true' ? t('Yes') : t('No');
  if (field === 'timeWindow') return windowLabel(t, value as TimeWindow) ?? value;
  if (field === 'refusalReasonCode') return REFUSAL_REASON_CODES.includes(value as RefusalReasonCode) ? refusalReasonLabel(t, value as RefusalReasonCode) : value;
  if (field === 'status') return interventionStatusLabel(t, value);
  if (field === 'tariff' || field === 'travelFee') {
    const cents = Number(value);
    return Number.isFinite(cents) ? `${centsText(cents, activeLocale())} €` : value;
  }
  if (field === 'sentOutsidePerimeter') return value === 'true' ? t('Yes') : t('No');
  if (field === 'role') return isValidRole(value) ? roleLabel(t, value) : value;
  if (field === 'mode') return modeLabel(t, value);
  if (field === 'permissions') return permissionLabel(t, value);
  if (field === 'serviceDesks' || field === 'ticketCreationDesks') return value === '*' ? t('All Service Desks') : value;
  if (field === 'onExceed') return value === 'BLOCK' ? t('Block') : value === 'WARN' ? t('Warn') : value;
  if (field === 'periodType') return quotaPeriodLabel(t, value);
  if (field === 'decisionRecipients') return decisionRecipientLabel(t, value);
  return value;
};

/** CA46 — same rule the backend filter uses: a system event is a NULL actor tagged in context.
 * `context` is read defensively (`?.`): a malformed or absent row must never throw (never-crash). */
export const isSystemEvent = (event: { actor: AuditEventActor | null; context: Record<string, unknown> | null }): boolean =>
  event.actor === null && event.context?.source === 'system';

/** J5/CA45/CA46 — actor null is either the system (`isSystemEvent`) or a SQL-deleted account; a
 * real actor acting `context.impersonatedBy` shows both names. `context` is optional/nullable so
 * every pre-Task-7 caller keeps compiling. */
export const actorLabel = (t: TFunction, actor: AuditEventActor | null, context: Record<string, unknown> | null = {}): string => {
  if (!actor) return isSystemEvent({ actor, context }) ? t('System') : t('Deleted user');
  const impersonatedBy = context?.impersonatedBy as { name?: unknown } | undefined;
  if (typeof impersonatedBy?.name === 'string') return t('{{name}} (via {{via}})', { name: actor.name, via: impersonatedBy.name });
  return actor.name;
};

export const isTechnicianActor = (actor: AuditEventActor | null): boolean => actor?.role === ROLES.TECHNICIAN;

interface EntityLinkDef {
  permission: string;
  path: (event: AdminAuditEvent) => string | null;
}

/** J19 — one row's link target per `entityType`. Lots 3-5 add rows here. */
const ENTITY_LINKS: Record<string, EntityLinkDef> = {
  // CA48 — a user row always points at the list, not a per-account page (none exists).
  user: { permission: PERMISSIONS.DASHBOARD_USERS_VIEW, path: () => '/admin' },
  technician: {
    permission: PERMISSIONS.DASHBOARD_TECHNICIANS_VIEW,
    path: (event) => `/admin/technicians/${event.entityId}`,
  },
  intervention_request: {
    permission: PERMISSIONS.DASHBOARD_ISSUES_VIEW,
    path: (event) => (typeof event.context?.issueKey === 'string' ? `/admin/issues/${encodeURIComponent(event.context.issueKey)}` : null),
  },
  // CA54 — a referential row always points at its list, never a per-value page a deleted value would strand.
  specialty: { permission: PERMISSIONS.DASHBOARD_REFERENTIALS_VIEW, path: () => '/admin/referentials/specialties' },
  expertise: { permission: PERMISSIONS.DASHBOARD_REFERENTIALS_VIEW, path: () => '/admin/referentials/specialties' },
  geo_subdivision: { permission: PERMISSIONS.DASHBOARD_REFERENTIALS_VIEW, path: () => '/admin/referentials/geography' },
  country: { permission: PERMISSIONS.DASHBOARD_REFERENTIALS_VIEW, path: () => '/admin/referentials/geography' },
  validation_group: { permission: PERMISSIONS.DASHBOARD_GROUPS_VIEW, path: () => '/admin/groups' },
  quota: { permission: PERMISSIONS.DASHBOARD_QUOTAS_VIEW, path: () => '/admin/quotas' },
  ticket: { permission: PERMISSIONS.DASHBOARD_ISSUES_VIEW, path: (event) => `/admin/issues/${encodeURIComponent(event.entityId)}` },
};

/** CA48 — an unknown `entityType`, a missing permission, or a required context field missing
 * (e.g. `issueKey`) all give `null` rather than a broken link. */
export const entityLink = (event: AdminAuditEvent, can: (permission: string) => boolean): string | null => {
  const link = ENTITY_LINKS[event.entityType];
  if (!link || !can(link.permission)) return null;
  return link.path(event);
};

/** J5 — a masked field never carries a value: phone, addresses, password. */
export const maskedValueLabel = (t: TFunction): string => t('Value not kept');

type SummaryEvent = Pick<AdminAuditEvent, 'action' | 'changes' | 'entityType' | 'context'>;

/** One counter of an import's `context.counts` (J7/J8); missing or malformed reads 0, never a crash. */
export const importCount = (event: Pick<AdminAuditEvent, 'context'>, key: string): number => {
  const value = (event.context?.counts as Record<string, unknown> | undefined)?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
};

/** An F9 parent's breakdown, failures included (CA8). */
export const technicianImportSummary = (t: TFunction, event: Pick<AdminAuditEvent, 'context'>): string =>
  t('{{created}} created · {{updated}} updated · {{excluded}} excluded · {{reinstated}} reinstated · {{failed}} failed', {
    created: importCount(event, 'created'),
    updated: importCount(event, 'updated'),
    excluded: importCount(event, 'excluded'),
    reinstated: importCount(event, 'reinstated'),
    failed: importCount(event, 'failed'),
  });

export const textOr = (value: unknown, fallback = '—'): string => (typeof value === 'string' && value ? value : fallback);

/** A realigned ticket's `changes.sector`; missing or masked reads « — » (never-crash). */
export const realignedSector = (event: Pick<AdminAuditEvent, 'changes'>): { from: string; to: string } => {
  const change = event.changes?.sector;
  if (!change || isMaskedChange(change)) return { from: '—', to: '—' };
  return { from: textOr(change.from), to: textOr(change.to) };
};

/** A realigned ticket's child row (8b), whose drawer is `RealignedTicketDetail` (8c). */
export const isRealignedTicket = (event: Pick<AdminAuditEvent, 'action' | 'entityType'>): boolean =>
  event.action === 'realign' && event.entityType === REALIGNED_TICKET_ENTITY;

/** A realigned ticket's outcome, from `context.status` (8b pill, 8c dot). */
export const realignResult = (t: TFunction, event: Pick<AdminAuditEvent, 'context'>): { label: string; failed: boolean } =>
  event.context?.status === REALIGNED_FAILED_STATUS ? { label: t('Failed'), failed: true } : { label: t('Realigned'), failed: false };

/** A realignment run's moves, stores, failures and interruption (8a): « SP1 → SP1 MSN, SP2 → SP3 · 12 magasins · 2 échecs ». */
export const realignmentSummary = (t: TFunction, event: Pick<AdminAuditEvent, 'context'>): string => {
  const transitions = event.context?.transitions;
  const moves = (Array.isArray(transitions) ? transitions : []).map((m) => `${textOr(m?.from)} → ${textOr(m?.to)}`).join(', ');
  const failed = importCount(event, 'failed');
  const stores = t('{{count}} store', { count: importCount(event, 'stores') });
  const interrupted = event.context?.outcome === 'interrupted' ? t('interrupted') : '';
  return [moves, stores, failed ? t('{{count}} failures', { count: failed }) : '', interrupted].filter(Boolean).join(' · ');
};

/** A referential's own noun for the summary (e.g. "Deleted {{entity}}"); `null` elsewhere. */
const referentialNoun = (t: TFunction, entityType: string): string | null => {
  switch (entityType) {
    case 'specialty':
      return t('the specialty');
    case 'expertise':
      return t('the expertise');
    case 'country':
      return t('the country');
    case 'geo_subdivision':
      return t('the subdivision');
    case 'validation_group':
      return t('the validation group');
    case 'quota':
      return t('the quota');
    default:
      return null;
  }
};

/** A user account event's wording; `null` for an action outside this set, falling through to
 * `summary`'s generic handling below. */
const userSummary = (t: TFunction, event: SummaryEvent): string | null => {
  switch (event.action) {
    case 'create':
      return t('Created the account');
    case 'grant':
      return t('Changed the rights');
    case 'deactivate':
      return t('Deactivated the account');
    case 'reactivate':
      return t('Reactivated the account');
    case 'resend_invitation':
      return t('Resent the invitation');
    case 'update': {
      const changedFields = Object.keys(event.changes);
      if (changedFields.length === 1 && changedFields[0] === 'password') return t('Changed the password');
      return t('Changed {{count}} field of the account', { count: changedFields.length });
    }
    default:
      return null;
  }
};

/** The verb clause following the bold actor name, e.g. "{Actor} " + summary(...). */
export const summary = (t: TFunction, event: SummaryEvent): string => {
  if (event.entityType === 'user') {
    const label = userSummary(t, event);
    if (label) return label;
  }
  const entity = referentialNoun(t, event.entityType);
  if (entity) {
    if (event.action === 'create') return t('Created {{entity}}', { entity });
    if (event.action === 'deactivate') return t('Deactivated {{entity}}', { entity });
    if (event.action === 'reactivate') return t('Reactivated {{entity}}', { entity });
    if (event.action === 'delete') return t('Deleted {{entity}}', { entity });
    if (event.action === 'update') {
      const changedFields = Object.keys(event.changes);
      if ('name' in event.changes) return t('Renamed {{entity}}', { entity });
      if (changedFields.length === 1 && changedFields[0] === 'specialties') return t('Changed the specialties of {{entity}}', { entity });
      if (changedFields.length === 1 && changedFields[0] === 'members') return t('Changed the members of the validation group');
      if (event.entityType === 'quota') return t('Changed {{count}} field of the quota', { count: changedFields.length });
    }
  }
  if (event.action === 'update' && event.entityType === 'intervention_request') {
    return t('Changed {{count}} term of the request', { count: Object.keys(event.changes).length });
  }
  switch (event.action) {
    case 'create':
      if (event.entityType === 'intervention_request') {
        return event.context?.replacesRequestId ? t('Reassigned the mission') : t('Solicited the technician');
      }
      return t('Created the record');
    case 'cancel': {
      const status = event.changes.status;
      const wasAccepted = status && !isMaskedChange(status) && status.from === 'accepted';
      return wasAccepted ? t('Released the technician') : t('Cancelled the solicitation');
    }
    case 'remind':
      return t('Reminded the technician');
    case 'resend_invitation':
      return t('Resent the invitation');
    case 'accept':
      return t('Accepted the mission');
    case 'decline':
      return t('Declined the request');
    case 'exclude':
      return t('Excluded the technician');
    case 'reinstate':
      return t('Reinstated the technician');
    case 'note':
      return t('Changed the comment');
    case 'import':
      if (event.entityType === 'quota_import') {
        return t('Imported a quota file — created: {{created}} · updated: {{updated}} · reactivated: {{reactivated}} · archived: {{archived}} · unchanged: {{unchanged}}', {
          created: importCount(event, 'created'),
          updated: importCount(event, 'updated'),
          reactivated: importCount(event, 'reactivated'),
          archived: importCount(event, 'archived'),
          unchanged: importCount(event, 'unchanged'),
        });
      }
      if (event.entityType === TECHNICIAN_IMPORT_ENTITY) return technicianImportSummary(t, event);
      return t('Updated the record via file import');
    case 'realign': {
      if (event.entityType === SECTOR_REALIGNMENT_ENTITY) return realignmentSummary(t, event);
      const { from, to } = realignedSector(event);
      return [`${from} → ${to}`, textOr(event.context?.store), realignResult(t, event).label].join(' · ');
    }
    case 'update': // non-intervention entities fall through to the same default wording
    default:
      return t('Changed {{count}} field of the record', { count: Object.keys(event.changes).length });
  }
};

const PARIS_TZ = 'Europe/Paris';

/** Stable YYYY-MM-DD grouping key in Paris local time (CA25) — a sort/group key, not display text. */
export const dayKey = (iso: string): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: PARIS_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(iso));

/** HH:mm in Paris local time, 24h, monospace-ready — locale-independent by design (§2). */
export const formatTime = (iso: string): string =>
  new Intl.DateTimeFormat('fr-FR', { timeZone: PARIS_TZ, hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso));

/** `YYYY-MM-DD` → `DD/MM/YYYY`, from a group's own key rather than reformatting the instant: it
 * can never land on a different day than the grouping already resolved (CA25). */
export const numericDate = (key: string): string => {
  const [y, m, d] = key.split('-');
  return `${d}/${m}/${y}`;
};
